import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Papa from 'papaparse'
import { eachDayOfInterval, endOfDay, format, parse, startOfDay, subDays } from 'date-fns'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import AssetWithdrawalModal, { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import Dropdown, { DropdownContentHeader, DropdownItem, DropdownTextInput } from '../../components/Dropdown'
import ExportBalanceForm from '../../components/ExportBalanceForm'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import Treasury from '../../state/Treasury'
import { RADOM_COLORS, WINDOW_SIZE } from '../../util/Constants'
import { getMethod, getPaymentMethods, ManagedPaymentMethod } from '../../util/Managed'
import { useTestMode, formatCurrency, errorToast, successToast } from '../../util/Util'
import { TokenTitle } from '../Funds'
import User from '../../state/User'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import AssetConversionRFQModal from '../../components/AssetConversionRFQModal'
import Radom, { ConversionPair, IPaymentMethod, ManagedBalance } from '../../state/Radom'
import ConvertIcon from '../../icons/ConvertIcon'
import Tooltip from '../../components/Tooltip'
import DashboardAreaChart, { DashboardAreaChartPoint } from '../../components/DashboardAreaChart'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SurfaceHeader,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceTitle
} from '../../components/DashboardPrimitives'

const BalanceHeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

const isSamePaymentMethod = (a: IPaymentMethod, b: IPaymentMethod): boolean => {
  return a.network === b?.network && a.token === b?.token
}

const managedMethodToPaymentMethod = (method: ManagedPaymentMethod): IPaymentMethod => ({
  network: method.hostChain?.name ?? method.name,
  token: method.hostChain?.tokenAddress
})

const getSwapMethodLabel = (method: ManagedPaymentMethod): string =>
  method.currencyName ?? method.name

const formatAssetAmount = (value: number | string | undefined): string => {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return '--'
  }

  return new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: 18
  }).format(numericValue)
}

interface SwapParams {
  fromNetwork: string
  fromToken?: string
  toNetwork?: string
  toToken?: string
  amount: number
};

interface BalanceRow {
  source: ManagedBalance
  method: ManagedPaymentMethod
  displayName: string
  networkLabel: string
  ticker: string
  balance: number
  isFiat: boolean
  isTestnet: boolean
  conversion?: { value: number }
  fiatValue: number
}

interface ExportedBalanceRow {
  date: string
  network: string
  token?: string
  ticker: string
  balance: string
}

type BalanceHistoryRangeKey = '7d' | '30d' | '90d' | '1y'

const BALANCE_HISTORY_RANGES: Array<{
  key: BalanceHistoryRangeKey
  label: string
  days: number
}> = [
  { key: '7d', label: '7D', days: 7 },
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  { key: '1y', label: '1Y', days: 365 }
]

const parseBalanceHistoryDate = (value: string): Date | undefined => {
  if (!value) return undefined

  const dailyDate = parse(value, 'dd/MM/yyyy', new Date())
  if (!Number.isNaN(dailyDate.getTime())) {
    return startOfDay(dailyDate)
  }

  const monthlyDate = parse(value, 'MM/yyyy', new Date())
  if (!Number.isNaN(monthlyDate.getTime())) {
    return startOfDay(monthlyDate)
  }

  return undefined
}

const performSwapEstimate = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<{ estimatedAmount: number }> => {
  const rates = await Radom.getSwapRates({
    from: {
      network: fromNetwork,
      token: fromToken
    },
    into: {
      network: toNetwork ?? '',
      token: toToken
    },
    amount
  })

  return {
    estimatedAmount: rates.amount
  }
}

const performSwap = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<void> => {
  await Radom.initiateSwap({
    from: {
      network: fromNetwork,
      token: fromToken
    },
    into: {
      network: toNetwork ?? '',
      token: toToken
    },
    amount
  })
}

const Overview = observer((): ReactElement => {
  const [testMode] = useTestMode()

  const [modalOpen, setModalOpen] = useState(false)
  const [exportBalanceModalOpen, setExportBalanceModalOpen] = useState(false)
  const [assetToWithdraw, setAssetToWithdraw] = useState<WithdrawableAsset>({
    maxAmount: '',
    network: 'Ethereum'
  })

  const [assetToConvertFrom, setAssetToConvertFrom] = useState<IPaymentMethod | null>(null)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [conversionPairs, setConversionPairs] = useState<ConversionPair[]>([])
  const [balanceHistory, setBalanceHistory] = useState<DashboardAreaChartPoint[]>([])
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(false)
  const [selectedBalanceRange, setSelectedBalanceRange] = useState<BalanceHistoryRangeKey>('30d')

  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapAmount, setSwapAmount] = useState<string>('')
  const [swapFromBalance, setSwapFromBalance] = useState<string>('')
  const [selectedSwapInto, setSelectedSwapInto] = useState<ManagedPaymentMethod | null>(null)
  const [swapEstimate, setSwapEstimate] = useState<{ estimatedAmount: number } | null>(null)
  const [swapLoading, setSwapLoading] = useState(false)
  const [swapEstimating, setSwapEstimating] = useState(false)
  const [swapStep, setSwapStep] = useState<'compose' | 'confirm'>('compose')
  const [swapSourceQuery, setSwapSourceQuery] = useState('')
  const [swapTargetQuery, setSwapTargetQuery] = useState('')
  const [swapEstimateMessage, setSwapEstimateMessage] = useState('')
  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const activeBalanceRange = BALANCE_HISTORY_RANGES.find(range => range.key === selectedBalanceRange) ??
    BALANCE_HISTORY_RANGES[1]

  useEffect(() => {
    Treasury.loadBalances()

    const fetchConversionPairs = async (): Promise<void> => {
      try {
        const response = await Radom.getConversionPairs()
        setConversionPairs(response)
      } catch (err) {
        console.error('Failed to get conversion pairs', err.response?.data?.error || err.message)
      }
    }

    fetchConversionPairs()
  }, [])

  useEffect(() => {
    if (Treasury.priceQuotes.length <= 0) {
      return
    }

    let cancelled = false

    const loadBalanceHistory = async (): Promise<void> => {
      setBalanceHistoryLoading(true)
      try {
        const historyEnd = endOfDay(new Date())
        const historyStart = startOfDay(subDays(historyEnd, activeBalanceRange.days - 1))
        const csv = await Radom.exportBalance(
          historyStart.toISOString(),
          historyEnd.toISOString(),
          'daily'
        )

        const parsed = Papa.parse<ExportedBalanceRow>(csv, {
          header: true,
          skipEmptyLines: true
        })

        const totalsByDate = new Map<number, number>()
        parsed.data.forEach((row) => {
          const parsedDate = parseBalanceHistoryDate(row.date)
          if (!parsedDate) return

          const method = getMethod(row.network, row.token)
          const isVisibleInCurrentMode = testMode ? !!method.isTestnet : !method.isTestnet
          if (!isVisibleInCurrentMode) return

          const numericBalance = Number(row.balance)
          if (Number.isNaN(numericBalance)) return

          const isFiat = row.network === 'Fiat' || !!method.isFiat
          const conversion = Treasury.priceQuotes.find(p => p.from.toLowerCase() === row.ticker.toLowerCase())
          const estimatedValue = isFiat && row.ticker === defaultCurrency
            ? numericBalance
            : conversion
              ? numericBalance * Number(conversion.value)
              : 0

          const dayKey = parsedDate.getTime()
          totalsByDate.set(dayKey, (totalsByDate.get(dayKey) ?? 0) + estimatedValue)
        })

        let lastKnownTotal = 0
        const filledSeries = eachDayOfInterval({ start: historyStart, end: historyEnd }).map((day) => {
          const dayKey = startOfDay(day).getTime()
          const dayValue = totalsByDate.get(dayKey)
          if (typeof dayValue === 'number') {
            lastKnownTotal = dayValue
          }

          return {
            x: dayKey,
            y: lastKnownTotal
          }
        })

        if (!cancelled) {
          setBalanceHistory(filledSeries)
        }
      } catch (err) {
        console.error('Failed to load balance history', err)
        if (!cancelled) {
          setBalanceHistory([])
        }
      } finally {
        if (!cancelled) {
          setBalanceHistoryLoading(false)
        }
      }
    }

    loadBalanceHistory()

    return () => {
      cancelled = true
    }
  }, [testMode, defaultCurrency, Treasury.priceQuotes.length, activeBalanceRange.days])

  const onClickWithdraw = (w: WithdrawableAsset): void => {
    setAssetToWithdraw(w)
    setModalOpen(true)
  }

  const onOpenConvert = (w: WithdrawableAsset): void => {
    setAssetToConvertFrom(w)
    setConvertModalOpen(true)
  }

  const onOpenSwap = (w: WithdrawableAsset): void => {
    setAssetToConvertFrom(w)
    setSwapAmount('')
    setSwapFromBalance(w.maxAmount)
    setSelectedSwapInto(null)
    setSwapEstimate(null)
    setSwapStep('compose')
    setSwapSourceQuery('')
    setSwapTargetQuery('')
    setSwapEstimateMessage('')
    setSwapModalOpen(true)
  }

  const possibleIntoAssets = conversionPairs
    .filter(({ from }) => assetToConvertFrom && isSamePaymentMethod(from, assetToConvertFrom))
    .map(({ into }) => into)

  const swapSourceMethods = useMemo(
    () => getPaymentMethods()
      .filter(m =>
        m.isSwapEnabled &&
        ((testMode && m.isTestnet) || (!testMode && !m.isTestnet)) &&
        !m.isFiat &&
        !m.isHidden &&
        !m.isDisabled
      )
      .sort((a, b) => getSwapMethodLabel(a).localeCompare(getSwapMethodLabel(b))),
    [testMode]
  )

  const swapTargetMethods = useMemo(
    () => swapSourceMethods
      .filter(m => !assetToConvertFrom || !isSamePaymentMethod(managedMethodToPaymentMethod(m), assetToConvertFrom)),
    [assetToConvertFrom, swapSourceMethods]
  )

  const swapFromMethod = assetToConvertFrom ? getMethod(assetToConvertFrom.network, assetToConvertFrom.token) : null
  const swapMaxAmountNumber = Number(swapFromBalance || 0)
  const selectedSwapIntoMethod = selectedSwapInto ? managedMethodToPaymentMethod(selectedSwapInto) : null

  const recalculateSwapEstimate = async (
    nextAmount: string,
    nextInto: ManagedPaymentMethod | null,
    fromAsset: IPaymentMethod | null = assetToConvertFrom
  ): Promise<void> => {
    const fromMethod = fromAsset ? getMethod(fromAsset.network, fromAsset.token) : null
    const fromConversion = fromMethod
      ? Treasury.priceQuotes.find(p => p.from.toLowerCase() === fromMethod.ticker.toLowerCase())
      : undefined

    if (!fromAsset || !nextInto || !fromConversion) {
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      return
    }

    const numeric = Number(nextAmount)
    if (!nextAmount || Number.isNaN(numeric) || numeric <= 0) {
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      return
    }

    const fiatValue = numeric * fromConversion.value
    if (fiatValue < 0) {
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      return
    }

    setSwapEstimating(true)
    setSwapEstimateMessage('')
    try {
      const estimate = await performSwapEstimate({
        fromNetwork: fromAsset.network,
        fromToken: fromAsset.token,
        toNetwork: nextInto?.hostChain?.name ?? nextInto?.name,
        toToken: nextInto?.hostChain?.tokenAddress ?? undefined,
        amount: numeric
      })
      setSwapEstimate(estimate)
    } catch (err) {
      setSwapEstimate(null)
      setSwapEstimateMessage(err.response?.data?.error || 'Estimate unavailable for this pair right now.')
    } finally {
      setSwapEstimating(false)
    }
  }

  const balanceRows = Treasury.managedBalances
    .filter(b => Number(b.balance) > 0)
    .map((b): BalanceRow => {
      const method = getMethod(b.network, b.token)
      const isFiat = b.network === 'Fiat' || !!method.isFiat
      const ticker = isFiat ? (b.token ?? method.ticker) : method.ticker
      const conversion = Treasury.priceQuotes
        .find(p => p.from.toLowerCase() === ticker.toLowerCase())
      const balance = Number(b.balance)
      const fiatValue = isFiat && ticker === defaultCurrency
        ? balance
        : conversion
          ? balance * conversion.value
          : 0

      return {
        source: b,
        method,
        displayName: isFiat ? `${ticker} balance` : method.currencyName ?? method.name,
        networkLabel: isFiat ? ticker : `${ticker} on ${method.hostChain?.name ?? method.name}`,
        ticker,
        balance,
        isFiat,
        isTestnet: !!method.isTestnet,
        conversion,
        fiatValue
      }
    })
    .filter(row => testMode ? row.isTestnet : !row.isTestnet)
    .sort((a, b) => b.fiatValue - a.fiatValue)

  const selectedSwapIntoBalanceRow = selectedSwapIntoMethod
    ? balanceRows.find((row) => isSamePaymentMethod(row.source, selectedSwapIntoMethod))
    : undefined
  const filteredSwapSourceMethods = swapSourceMethods.filter((method) => {
    if (!swapSourceQuery.trim()) {
      return true
    }

    const normalizedQuery = swapSourceQuery.trim().toLowerCase()
    return getSwapMethodLabel(method).toLowerCase().includes(normalizedQuery) ||
      method.ticker.toLowerCase().includes(normalizedQuery) ||
      method.hostChain?.name.toLowerCase().includes(normalizedQuery)
  })
  const filteredSwapTargetMethods = swapTargetMethods.filter((method) => {
    if (!swapTargetQuery.trim()) {
      return true
    }

    const normalizedQuery = swapTargetQuery.trim().toLowerCase()
    return getSwapMethodLabel(method).toLowerCase().includes(normalizedQuery) ||
      method.ticker.toLowerCase().includes(normalizedQuery) ||
      method.hostChain?.name.toLowerCase().includes(normalizedQuery)
  })
  const hasExecutableSwapBalance = swapMaxAmountNumber > 0
  const canReviewSwap = Boolean(
    !swapLoading &&
    !swapEstimating &&
    assetToConvertFrom &&
    selectedSwapInto &&
    swapEstimate &&
    swapAmount &&
    hasExecutableSwapBalance &&
    Number(swapAmount) > 0 &&
    Number(swapAmount) <= swapMaxAmountNumber
  )
  const swapEstimatedRate = swapEstimate && Number(swapAmount) > 0
    ? swapEstimate.estimatedAmount / Number(swapAmount)
    : undefined

  const assetSummaries = Object.values(balanceRows.reduce<Record<string, {
    ticker: string
    fiatValue: number
  }>>((assets, row) => {
    const current = assets[row.ticker] ?? {
      ticker: row.ticker,
      fiatValue: 0
    }

    assets[row.ticker] = {
      ...current,
      fiatValue: current.fiatValue + row.fiatValue
    }

    return assets
  }, {})).sort((a, b) => b.fiatValue - a.fiatValue)
  const totalEstimatedValue = assetSummaries.reduce((total, row) => total + row.fiatValue, 0)
  const currentTrendValue = balanceHistory.length > 0
    ? balanceHistory[balanceHistory.length - 1].y
    : totalEstimatedValue
  const previousTrendValue = balanceHistory.length > 0
    ? balanceHistory[0].y
    : 0
  const balanceTrendDelta = currentTrendValue - previousTrendValue
  const balanceTrendDeltaPct = previousTrendValue > 0 ? balanceTrendDelta / previousTrendValue : undefined
  const topThreeEstimatedValue = assetSummaries.slice(0, 3).reduce((total, row) => total + row.fiatValue, 0)
  const topThreeShare = totalEstimatedValue > 0 ? topThreeEstimatedValue / totalEstimatedValue : 0
  const topAssetSummary = assetSummaries[0]
  const balanceHistoryWindowLabel = balanceHistory.length > 0
    ? `${format(new Date(balanceHistory[0].x), 'MMM d')} - ${format(new Date(balanceHistory[balanceHistory.length - 1].x), 'MMM d, yyyy')}`
    : activeBalanceRange.label
  const allocationPalette = ['#3464e6', '#15957b', '#7c3aed', '#0891b2', '#f59e0b', '#94a3b8']
  const allocationRows = assetSummaries
    .filter(row => row.fiatValue > 0)
    .slice(0, 5)
    .map((row, index) => ({
      ...row,
      color: allocationPalette[index % allocationPalette.length],
      share: totalEstimatedValue > 0 ? row.fiatValue / totalEstimatedValue : 0
    }))
  const remainingAllocationValue = assetSummaries
    .slice(5)
    .reduce((total, row) => total + row.fiatValue, 0)
  if (remainingAllocationValue > 0) {
    allocationRows.push({
      ticker: 'Other',
      fiatValue: remainingAllocationValue,
      color: allocationPalette[allocationPalette.length - 1],
      share: totalEstimatedValue > 0 ? remainingAllocationValue / totalEstimatedValue : 0
    })
  }

  const flipSwapPair = async (): Promise<void> => {
    if (!selectedSwapInto || !swapFromMethod) {
      return
    }

    const nextSource = managedMethodToPaymentMethod(selectedSwapInto)
    const nextTarget = swapFromMethod
    const nextBalance = selectedSwapIntoBalanceRow?.source.balance ?? '0'
    const nextAmount = !swapAmount
      ? ''
      : Number(nextBalance) > 0 && Number(swapAmount) > Number(nextBalance)
        ? String(nextBalance)
        : swapAmount

    setAssetToConvertFrom(nextSource)
    setSwapFromBalance(String(nextBalance))
    setSelectedSwapInto(nextTarget)
    setSwapAmount(nextAmount)
    setSwapEstimate(null)
    setSwapEstimateMessage('')
    setSwapStep('compose')
    setSwapSourceQuery('')
    setSwapTargetQuery('')

    if (nextAmount) {
      await recalculateSwapEstimate(nextAmount, nextTarget, nextSource)
    }
  }

  const renderSwapMethodOption = (method: ManagedPaymentMethod): ReactElement => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 30 }}>
      <PaymentMethodDisplay
        method={managedMethodToPaymentMethod(method)}
        paymentMethod={method}
        excludeName
        size={24}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>{method.ticker}</span>
        <span style={{ color: '#747894', fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getSwapMethodLabel(method)}
        </span>
      </div>
    </div>
  )

  return <DashboardPage>
    <PageHeaderRow>
      <PageHeader
        title='Balances' />
      <PageActionRow>
        <SecondaryButton onClick={() => setExportBalanceModalOpen(true)}>
          Export report
        </SecondaryButton>
      </PageActionRow>
    </PageHeaderRow>

    <Surface style={{ gap: 16, padding: '18px 20px' }}>
      <BalanceHeroGrid>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, minWidth: 0 }}>
          <SurfaceHeader style={{ gap: 9 }}>
            <div style={{ minWidth: 0 }}>
              <SurfaceTitle>Available balance</SurfaceTitle>
            </div>
            <InlinePill $tone={testMode ? 'amber' : 'green'}>
              {testMode ? 'Test mode' : 'Live balances'}
            </InlinePill>
          </SurfaceHeader>
          <SummaryValue style={{ fontSize: 36, lineHeight: 1.04 }}>
            {Treasury.loading
              ? <LoadingBar style={{ height: 38, width: 180 }} />
              : formatCurrency(totalEstimatedValue, defaultCurrency)}
          </SummaryValue>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <InlinePill $tone={balanceTrendDelta >= 0 ? 'green' : 'amber'}>
              {balanceTrendDelta >= 0 ? '+' : '-'}
              {formatCurrency(Math.abs(balanceTrendDelta), defaultCurrency)}
            </InlinePill>
            {typeof balanceTrendDeltaPct === 'number' &&
                <span style={{ color: '#7d8492', fontSize: 12.5, lineHeight: 1.4 }}>
                  {`${balanceTrendDeltaPct >= 0 ? '+' : ''}${(balanceTrendDeltaPct * 100).toFixed(1)}% across ${activeBalanceRange.label}`}
                </span>}
          </div>
          <SummaryMeta>{balanceHistoryWindowLabel}</SummaryMeta>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, minWidth: 0 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            {BALANCE_HISTORY_RANGES.map((range) =>
              <SecondaryButton
                key={range.key}
                onClick={() => setSelectedBalanceRange(range.key)}
                style={{
                  background: range.key === selectedBalanceRange ? '#eff4ff' : '#fff',
                  borderColor: range.key === selectedBalanceRange ? '#d6e1ff' : '#e8edf5',
                  color: range.key === selectedBalanceRange ? '#2447a6' : '#556072',
                  borderRadius: 999,
                  fontSize: 11.5,
                  minHeight: 29,
                  padding: '5px 10px'
                }}>
                {range.label}
              </SecondaryButton>
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            <DashboardAreaChart
              accentColor='#3464e6'
              data={balanceHistory}
              height={202}
              isLoading={balanceHistoryLoading}
              showYAxis
              formatXAxisLabelFn={(value) => format(new Date(value), 'MMM d')}
              formatXLabelFn={(value) => format(new Date(value), 'MMM d, yyyy')}
              formatYLabelFn={(value) => formatCurrency(value, defaultCurrency)}
            />
          </div>
        </div>
      </BalanceHeroGrid>

      <SummaryStrip style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {[
          {
            label: `${activeBalanceRange.label} change`,
            value: Treasury.loading
              ? 'loading'
              : `${balanceTrendDelta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(balanceTrendDelta), defaultCurrency)}`,
            meta: balanceHistoryWindowLabel
          },
          {
            label: 'Largest position',
            value: Treasury.loading
              ? 'loading'
              : topAssetSummary
                ? formatCurrency(topAssetSummary.fiatValue, defaultCurrency)
                : 'None',
            meta: topAssetSummary ? topAssetSummary.ticker : 'No settled funds yet'
          },
          {
            label: 'Top 3 concentration',
            value: Treasury.loading
              ? 'loading'
              : totalEstimatedValue > 0
                ? `${(topThreeShare * 100).toFixed(topThreeShare >= 0.995 ? 0 : 1)}%`
                : 'None',
            meta: totalEstimatedValue > 0 ? 'Share of portfolio value' : 'No visible concentration yet'
          }
        ].map((item) =>
          <SummaryStripItem
            key={item.label}
            style={{ gap: 5 }}>
            <SummaryLabel>{item.label}</SummaryLabel>
            <SummaryValue style={{ fontSize: 16.5 }}>
              {item.value === 'loading'
                ? <LoadingBar style={{ height: 24, width: 110 }} />
                : item.value}
            </SummaryValue>
            <SummaryMeta>{item.meta}</SummaryMeta>
          </SummaryStripItem>
        )}
      </SummaryStrip>
    </Surface>

    <Surface style={{ gap: 14, padding: '16px 18px' }}>
      {!Treasury.loading && allocationRows.length > 0 &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500 }}>Portfolio allocation</span>
              <span style={{ color: '#7d8492', fontSize: 12.25 }}>
                Current asset mix by estimated value in {defaultCurrency}.
              </span>
            </div>
            <div style={{
              alignItems: 'stretch',
              background: '#edf1f7',
              borderRadius: 999,
              display: 'flex',
              height: 7,
              overflow: 'hidden'
            }}>
              {allocationRows.map((row) =>
                <div
                  key={row.ticker}
                  style={{
                    background: row.color,
                    minWidth: `${Math.max(row.share * 100, 3)}%`
                  }}
                />
              )}
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '11px 16px'
            }}>
              {allocationRows.map((row) =>
                <div
                  key={`${row.ticker}-legend`}
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 7,
                    minWidth: 0
                  }}>
                  <div style={{
                    background: row.color,
                    borderRadius: 999,
                    flexShrink: 0,
                    height: 7,
                    width: 7
                  }} />
                  <span style={{ color: '#20242d', fontSize: 12.25, fontWeight: 500 }}>{row.ticker}</span>
                  <span style={{ color: '#7d8492', fontSize: 12.25 }}>
                    {formatCurrency(row.fiatValue, defaultCurrency)}
                  </span>
                  <span style={{ color: '#4b5565', fontSize: 12.25, fontWeight: 500 }}>
                    {(row.share * 100).toFixed(row.share >= 0.1 ? 0 : 1)}%
                  </span>
                </div>
              )}
            </div>
          </div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: '#20242d', fontSize: 13.75, fontWeight: 500 }}>Asset balances</span>
        <span style={{ color: '#7d8492', fontSize: 12.25 }}>
            Review available funds by network before withdrawing, swapping, or converting.
        </span>
      </div>
      <Table
        headers={['Asset', 'Balance', 'Exchange rate', 'Actions'].map(h => <span key={h}>{h}</span>)}
        borderless
        tableContainerStyle={{
          gridTemplateColumns: 'minmax(260px, 1.35fr) minmax(180px, 1fr) minmax(160px, 0.8fr) minmax(260px, auto)'
        }}
        items={
          balanceRows.map(row => [
            <div key="token" style={{ display: 'flex', flexDirection: 'column', fontSize: 14 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <PaymentMethodDisplay
                  method={{ network: row.source.network, token: row.source.token }}
                  excludeName
                  size={30} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TokenTitle>{row.displayName}</TokenTitle>
                  <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                    {row.networkLabel}
                  </span>
                </div>
              </div>
            </div>,
            <div key="value" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span>
                {new Intl.NumberFormat('en-US', {
                  maximumSignificantDigits: 18
                }).format(row.balance)}
              </span>
              {row.conversion || row.isFiat
                ? <span style={{ opacity: 0.5 }}>
                    ≈ {formatCurrency(row.fiatValue, defaultCurrency)}
                </span>
                : <LoadingBar style={{ height: 10 }} />}
            </div>,

            <div key='conversion'>
              {row.conversion
                ? <span>{formatCurrency(Number(row.conversion.value), defaultCurrency)} {defaultCurrency}</span>
                : row.isFiat
                  ? <span>1 {defaultCurrency}</span>
                  : <LoadingBar style={{ height: 16 }} />}
            </div>,

            <div key="withdraw" style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              width: '100%',
              flexWrap: 'wrap'
            }}>
              {!row.isFiat &&
                  <SecondaryButton
                    onClick={() => {
                      onClickWithdraw({
                        network: row.source.network,
                        token: row.source.token,
                        maxAmount: row.source.balance
                      })
                    }}
                    style={{ fontSize: 12.75 }}>
                    Withdraw
                  </SecondaryButton>}
              {User.organization?.features?.isSwapEnabled && !row.isFiat && row.method.isSwapEnabled && (
                row.fiatValue > 0
                  ? (
                    <SecondaryButton
                      onClick={() => {
                        onOpenSwap({
                          network: row.source.network,
                          token: row.source.token,
                          maxAmount: row.source.balance
                        })
                      }}
                      style={{ fontSize: 12.75 }}>
                        Swap
                    </SecondaryButton>
                  )
                  : (
                    <Tooltip message={`Minimum amount to swap is 100 ${defaultCurrency}`}>
                      <SecondaryButton disabled style={{ fontSize: 12.75 }}>
                          Swap
                      </SecondaryButton>
                    </Tooltip>
                  )
              )}
              {User.organization?.features?.isConversionEnabled &&
                  conversionPairs.map(({ from }) => from).find(x => isSamePaymentMethod(x, row.source)) &&
                  <Tooltip message='Convert balance'>
                    <SecondaryButton
                      onClick={() => {
                        onOpenConvert({
                          network: row.source.network,
                          token: row.source.token,
                          maxAmount: row.source.balance
                        })
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12.75
                      }}>
                      <ConvertIcon
                        stroke={'#687183'}
                        strokeWidth={2.1}
                        style={{ width: 14, height: 14 }} />
                      Convert
                    </SecondaryButton>
                  </Tooltip>}
            </div>
          ])
        }
        isLoading={Treasury.loading}
        emptyPlaceholderText="No balances yet. Incoming payments and deposits will appear here once funds settle."
      />
    </Surface>

    {
      modalOpen &&
      <AssetWithdrawalModal
        modalOpen={modalOpen}
        onModalOpen={open => setModalOpen(open)}
        assetToWithdraw={assetToWithdraw}
      />
    }

    {
      exportBalanceModalOpen &&
      <Modal visible={exportBalanceModalOpen} onClose={() => setExportBalanceModalOpen(false)} title='Export balance report' innerStyles={{ width: 820 }}>
        <ExportBalanceForm />
      </Modal>
    }

    {
      swapModalOpen && assetToConvertFrom &&
      <Modal
        visible={swapModalOpen}
        onClose={() => setSwapModalOpen(false)}
        innerStyles={{ width: 500 }}
        title='Swap assets'>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {swapStep === 'compose' &&
            <>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                padding: 10,
                borderRadius: 13,
                border: '1px solid #eceff3',
                background: '#fcfdff'
              }}>
                <div style={{
                  borderRadius: 13,
                  padding: 12,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e7ebf2',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</span>
                    <span style={{ fontSize: 12.25, color: '#6b7280' }}>
                      Available: {swapFromBalance} {swapFromMethod?.ticker}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 180 }}>
                      <input
                        value={swapAmount}
                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                          const raw = e.target.value
                          const numeric = Number(raw)
                          let clamped = raw

                          setSwapStep('compose')
                          setSwapEstimate(null)
                          setSwapEstimateMessage('')

                          if (!raw) {
                            setSwapAmount('')
                            return
                          }

                          if (!Number.isNaN(numeric) && swapMaxAmountNumber > 0 && numeric > swapMaxAmountNumber) {
                            clamped = swapFromBalance
                          }

                          setSwapAmount(clamped)
                          await recalculateSwapEstimate(clamped, selectedSwapInto)
                        }}
                        placeholder='0.0'
                        style={{
                          border: 'none',
                          background: 'transparent',
                          fontSize: 23,
                          fontWeight: 600,
                          lineHeight: 1.06,
                          outline: 'none',
                          padding: 0
                        }}
                      />
                      <span style={{ color: '#7d8492', fontSize: 12.25, lineHeight: 1.45 }}>
                        {hasExecutableSwapBalance
                          ? `Enter how much ${swapFromMethod?.ticker ?? 'asset'} you want to swap.`
                          : `No settled ${swapFromMethod?.ticker ?? 'asset'} balance yet. You can still preview rates for this pair.`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <SecondaryButton
                        type='button'
                        disabled={!hasExecutableSwapBalance}
                        onClick={async () => {
                          const clamped = swapFromBalance
                          setSwapStep('compose')
                          setSwapAmount(clamped)
                          setSwapEstimate(null)
                          setSwapEstimateMessage('')
                          await recalculateSwapEstimate(clamped, selectedSwapInto)
                        }}
                        style={{ minHeight: 34, padding: '0 12px' }}>
                        Max
                      </SecondaryButton>
                      <Dropdown
                        matchTriggerWidth
                        outerStyle={{ minHeight: 42, minWidth: 170 }}
                        selectedContent={
                          swapFromMethod
                            ? renderSwapMethodOption(swapFromMethod)
                            : <span style={{ fontSize: 13.5, color: '#747894' }}>Select asset</span>
                        }
                        dropdownContent={
                          <div>
                            <DropdownContentHeader style={{
                              position: 'sticky',
                              top: 0,
                              background: '#fff',
                              borderBottom: '1px solid #edf1f7',
                              zIndex: 1
                            }}>
                              <DropdownTextInput
                                autoFocus
                                value={swapSourceQuery}
                                onChange={(e) => setSwapSourceQuery((e.target as HTMLInputElement).value)}
                                placeholder='Search asset or ticker'
                              />
                            </DropdownContentHeader>
                            {filteredSwapSourceMethods.map((m, idx) => (
                              <DropdownItem
                                key={`${m.name}-${m.ticker}-${idx}`}
                                onClick={async () => {
                                  const nextSource = managedMethodToPaymentMethod(m)
                                  const nextBalance =
                                    balanceRows.find((row) => isSamePaymentMethod(row.source, nextSource))?.source.balance ?? '0'
                                  const nextTarget = selectedSwapInto &&
                                    isSamePaymentMethod(managedMethodToPaymentMethod(selectedSwapInto), nextSource)
                                    ? null
                                    : selectedSwapInto

                                  setSwapStep('compose')
                                  setAssetToConvertFrom(nextSource)
                                  setSwapFromBalance(String(nextBalance))
                                  setSelectedSwapInto(nextTarget)
                                  setSwapEstimate(null)
                                  setSwapEstimateMessage('')
                                  setSwapSourceQuery('')
                                  setSwapTargetQuery('')

                                  if (nextTarget) {
                                    await recalculateSwapEstimate(swapAmount, nextTarget, nextSource)
                                  }
                                }}
                                style={{ padding: '10px 11px' }}>
                                {renderSwapMethodOption(m)}
                              </DropdownItem>
                            ))}
                            {filteredSwapSourceMethods.length === 0 &&
                              <div style={{ color: '#747894', fontSize: 13, padding: '14px 12px' }}>
                                No matching assets found.
                              </div>}
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', margin: '-6px 0' }}>
                  <Tooltip
                    disabled={!!selectedSwapInto}
                    message='Choose the asset you want to receive first.'>
                    <span style={{ display: 'inline-flex' }}>
                      <SecondaryButton
                        type='button'
                        disabled={!selectedSwapInto}
                        onClick={() => {
                          void flipSwapPair()
                        }}
                        style={{
                          minHeight: 36,
                          minWidth: 36,
                          padding: 0,
                          borderRadius: 999
                        }}>
                        <ConvertIcon stroke='#111111' strokeWidth='1.6' style={{ width: 16, height: 16 }} />
                      </SecondaryButton>
                    </span>
                  </Tooltip>
                </div>

                <div style={{
                  borderRadius: 13,
                  padding: 12,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e7ebf2',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</span>
                    <span style={{ fontSize: 12.25, color: '#6b7280' }}>
                      {swapEstimating ? 'Refreshing quote...' : swapEstimate ? 'Estimated output' : 'Choose an asset'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 180 }}>
                      <span style={{ fontSize: 23, fontWeight: 600, lineHeight: 1.06, color: '#20242d' }}>
                        {swapEstimate
                          ? formatAssetAmount(swapEstimate.estimatedAmount)
                          : '--'}
                      </span>
                      <span style={{ color: '#7d8492', fontSize: 12.25, lineHeight: 1.45 }}>
                        {selectedSwapInto ? `Estimated ${selectedSwapInto.ticker} received before the final quote is locked.` : 'Pick the asset you want to receive.'}
                      </span>
                    </div>
                    <Dropdown
                      matchTriggerWidth
                      outerStyle={{ minHeight: 42, minWidth: 170 }}
                      selectedContent={
                        selectedSwapInto
                          ? renderSwapMethodOption(selectedSwapInto)
                          : <span style={{ fontSize: 13.5, color: '#747894' }}>Select asset</span>
                      }
                      dropdownContent={
                        <div>
                          <DropdownContentHeader style={{
                            position: 'sticky',
                            top: 0,
                            background: '#fff',
                            borderBottom: '1px solid #edf1f7',
                            zIndex: 1
                          }}>
                            <DropdownTextInput
                              autoFocus
                              value={swapTargetQuery}
                              onChange={(e) => setSwapTargetQuery((e.target as HTMLInputElement).value)}
                              placeholder='Search asset or ticker'
                            />
                          </DropdownContentHeader>
                          {filteredSwapTargetMethods.map((m, idx) => (
                            <DropdownItem
                              key={`${m.name}-${m.ticker}-${idx}`}
                              onClick={async () => {
                                setSwapStep('compose')
                                setSelectedSwapInto(m)
                                setSwapEstimate(null)
                                setSwapEstimateMessage('')
                                setSwapTargetQuery('')
                                await recalculateSwapEstimate(swapAmount, m)
                              }}
                              style={{ padding: '10px 11px' }}>
                              {renderSwapMethodOption(m)}
                            </DropdownItem>
                          ))}
                          {filteredSwapTargetMethods.length === 0 &&
                            <div style={{ color: '#747894', fontSize: 13, padding: '14px 12px' }}>
                              No matching assets found.
                            </div>}
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                padding: '11px 13px',
                borderRadius: 13,
                border: '1px solid #eceff3',
                background: '#f9fafb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#5f6675', fontSize: 12.5 }}>Estimated output</span>
                  <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>
                    {swapEstimate && selectedSwapInto ? `${formatAssetAmount(swapEstimate.estimatedAmount)} ${selectedSwapInto.ticker}` : '--'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#5f6675', fontSize: 12.5 }}>Estimated rate</span>
                  <span style={{ color: '#20242d', fontSize: 13, fontWeight: 500 }}>
                    {swapEstimatedRate && selectedSwapInto && swapFromMethod
                      ? `1 ${swapFromMethod.ticker} ≈ ${formatAssetAmount(swapEstimatedRate)} ${selectedSwapInto.ticker}`
                      : '--'}
                  </span>
                </div>
                <span style={{ color: '#747894', fontSize: 12, lineHeight: 1.5 }}>
                  {swapEstimateMessage || 'Quotes are estimates until you confirm the swap with our external liquidity partners.'}
                </span>
              </div>

              <PrimaryButton
                disabled={!canReviewSwap}
                style={{ width: '100%' }}
                onClick={() => setSwapStep('confirm')}>
                Review swap
              </PrimaryButton>
            </>}

          {swapStep === 'confirm' &&
            <>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: 12,
                borderRadius: 14,
                border: '1px solid #eceff3',
                background: '#fcfdff'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: '11px 13px',
                  borderRadius: 13,
                  border: '1px solid #eceff3',
                  background: '#ffffff'
                }}>
                  <span style={{ color: '#20242d', fontSize: 14.5, fontWeight: 500 }}>Review your estimated swap</span>
                  <span style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.5 }}>
                    Check the asset pair and estimated output before sending this swap for execution.
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                  padding: '13px',
                  borderRadius: 13,
                  border: '1px solid #eceff3',
                  background: '#ffffff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#5f6675', fontSize: 12.5 }}>You pay</span>
                    <span style={{ color: '#20242d', fontSize: 13.5, fontWeight: 500 }}>
                      {formatAssetAmount(swapAmount)} {swapFromMethod?.ticker}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#5f6675', fontSize: 12.5 }}>Estimated to receive</span>
                    <span style={{ color: '#20242d', fontSize: 13.5, fontWeight: 500 }}>
                      {swapEstimate && selectedSwapInto ? `${formatAssetAmount(swapEstimate.estimatedAmount)} ${selectedSwapInto.ticker}` : '--'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#5f6675', fontSize: 12.5 }}>Estimated rate</span>
                    <span style={{ color: '#20242d', fontSize: 13.5, fontWeight: 500 }}>
                      {swapEstimatedRate && selectedSwapInto && swapFromMethod
                        ? `1 ${swapFromMethod.ticker} ≈ ${formatAssetAmount(swapEstimatedRate)} ${selectedSwapInto.ticker}`
                        : '--'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#5f6675', fontSize: 12.5 }}>Available balance</span>
                    <span style={{ color: '#20242d', fontSize: 13.5, fontWeight: 500 }}>
                      {swapFromBalance} {swapFromMethod?.ticker}
                    </span>
                  </div>
                </div>

                <div style={{
                  padding: '11px 13px',
                  borderRadius: 13,
                  border: '1px solid #eceff3',
                  background: '#f9fafb',
                  color: '#5f6675',
                  fontSize: 12.25,
                  lineHeight: 1.5
                }}>
                  Estimated rates can move before execution. The final quote is locked only when the
                  {' '}swap is confirmed with our external liquidity partners.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                <SecondaryButton
                  onClick={() => setSwapStep('compose')}
                  style={{ width: '100%' }}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  isLoading={swapLoading}
                  disabled={!canReviewSwap}
                  style={{ width: '100%' }}
                  onClick={async () => {
                    if (!assetToConvertFrom || !selectedSwapInto || !swapEstimate) return
                    setSwapLoading(true)
                    try {
                      await performSwap({
                        fromNetwork: assetToConvertFrom.network,
                        fromToken: assetToConvertFrom.token,
                        toNetwork: selectedSwapInto.hostChain?.name ?? selectedSwapInto.name,
                        toToken: selectedSwapInto.hostChain?.tokenAddress ?? undefined,
                        amount: Number(swapAmount)
                      })
                      successToast('Swap completed')
                      setSwapModalOpen(false)
                      Treasury.loadBalances()
                    } catch (err) {
                      errorToast(err.response?.data?.error || err.message)
                    } finally {
                      setSwapLoading(false)
                    }
                  }}>
                  Confirm swap
                </PrimaryButton>
              </div>
            </>}
        </div>
      </Modal>
    }

    {
      convertModalOpen && assetToConvertFrom && possibleIntoAssets &&
      <AssetConversionRFQModal
        modalOpen={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        fromAsset={assetToConvertFrom}
        availableIntoAssets={possibleIntoAssets}
        selectedIntoAsset={possibleIntoAssets[0]}
        onConvert={async (quoteId) => {
          try {
            await Radom.acceptQuote(quoteId)
            successToast('Successfully converted assets')
            setConvertModalOpen(false)
            Treasury.loadBalances()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }}
      />
    }
  </DashboardPage>
})

export default Overview
